import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle, UserPlus } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const empty = { name: "", known_by: "", email: "", phone: "", dob: "", gender: "", club: "", reason: "" };
const field = "rounded-none border-navy/20 bg-white h-12 focus-visible:ring-2 focus-visible:ring-gold focus-visible:border-gold";
const POLICY_URL = "https://tabletennis.org.nz/about-us/our-constitution-policies";

export const MemberRegisterForm = () => {
  const [form, setForm] = useState(empty);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imr, setImr] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please enter your name and email."); return; }
    if (!agreed) { toast.error("Please confirm you have read and agree to the TTNZ privacy policy."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/register/member", { ...form, agreed_privacy: true });
      setImr(data.provisional_imr);
      setForm(empty); setAgreed(false);
      toast.success("Registration received!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  if (imr) {
    return (
      <div className="bg-white border border-navy/10 p-8 md:p-10 text-center" data-testid="register-success">
        <CheckCircle size={52} weight="fill" className="text-gold mx-auto" />
        <h3 className="font-heading text-3xl text-navy font-medium mt-4">Welcome to Te Manawa!</h3>
        <p className="mt-3 text-navy/70">Your registration has been received. Your provisional membership number is:</p>
        <p className="mt-4 font-mono text-2xl text-navy bg-cream inline-block px-6 py-3 border border-navy/10">{imr}</p>
        <p className="mt-4 text-sm text-navy/60">This is a provisional IMR pending confirmation with Table Tennis New Zealand. We'll be in touch soon.</p>
        <button onClick={() => setImr(null)} className="mt-6 text-sm font-semibold text-navy underline">Register another member</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="member-register-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div><label className="text-sm font-medium text-navy mb-2 block">Full name *</label>
          <Input data-testid="reg-name" value={form.name} onChange={set("name")} placeholder="Your full name" className={field} /></div>
        <div><label className="text-sm font-medium text-navy mb-2 block">Known by</label>
          <Input data-testid="reg-known-by" value={form.known_by} onChange={set("known_by")} placeholder="Nickname you play under (e.g. Kiwi name)" className={field} /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div><label className="text-sm font-medium text-navy mb-2 block">Email *</label>
          <Input data-testid="reg-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={field} /></div>
        <div><label className="text-sm font-medium text-navy mb-2 block">Phone</label>
          <Input data-testid="reg-phone" value={form.phone} onChange={set("phone")} placeholder="Optional" className={field} /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div><label className="text-sm font-medium text-navy mb-2 block">Date of birth</label>
          <Input data-testid="reg-dob" type="date" value={form.dob} onChange={set("dob")} className={field} />
          <span className="text-xs text-navy/50 mt-1 block">Required for age-group events</span></div>
        <div><label className="text-sm font-medium text-navy mb-2 block">Gender</label>
          <select data-testid="reg-gender" value={form.gender} onChange={set("gender")} className="w-full rounded-none border border-navy/20 bg-white h-12 px-3 focus:ring-2 focus:ring-gold outline-none">
            <option value="">Select…</option><option value="M">Male</option><option value="F">Female</option><option value="O">Other</option>
          </select></div>
      </div>
      <div><label className="text-sm font-medium text-navy mb-2 block">Club (if any)</label>
        <Input data-testid="reg-club" value={form.club} onChange={set("club")} placeholder="e.g. Kihikihi, Rotorua, Papāmoa…" className={field} /></div>
      <div><label className="text-sm font-medium text-navy mb-2 block">Reason for joining</label>
        <Textarea data-testid="reg-reason" value={form.reason} onChange={set("reason")} rows={3} placeholder="For fun, joining with whānau, health & fitness, community involvement, competition, regional representative honours…" className="rounded-none border-navy/20 bg-white focus-visible:ring-2 focus-visible:ring-gold focus-visible:border-gold" /></div>
      <label className="flex items-start gap-3 bg-cream/60 border border-navy/10 p-4 cursor-pointer">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="reg-privacy" className="mt-1 h-4 w-4 accent-gold shrink-0" />
        <span className="text-sm text-navy/80">Have you read and agree to the <a href={POLICY_URL} target="_blank" rel="noreferrer" data-testid="reg-privacy-link" className="text-navy font-semibold underline hover:text-gold">TTNZ privacy policy</a>? Te Manawa members agree to how their personal information is collected, used, stored and disclosed. *</span>
      </label>
      <button type="submit" disabled={loading} data-testid="reg-submit" className="inline-flex items-center gap-2 bg-navy text-white font-semibold px-8 py-4 rounded-sm transition-colors hover:bg-gold hover:text-navy disabled:opacity-60">
        <UserPlus size={18} /> {loading ? "Submitting…" : "Register as a member"}
      </button>
    </form>
  );
};
